"use client";

import { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { useSpotUser } from "@/utils/spotUser";
import { formatWorkspaceRef, parseWorkspaceRef } from "./workspaceRef";
import {
  WorkspaceRefChip,
  WorkspaceSecretRefPicker,
} from "./WorkspaceSecretRefPicker";

interface WorkspaceSecretRefFieldProps {
  name: string;
  label?: string | null;
  helperText?: string | null;
  tooltip?: string | null;
  disabled?: boolean;
  required?: boolean;
  /** Pixel offset from the right edge for the picker icon. Use to avoid
   *  overlapping with a trailing input control (e.g. the password eye). */
  rightOffsetPx?: number;
  /** The original input to render when the value is NOT a ref. */
  children: ReactNode;
}

/**
 * Wraps a form input so the user can either type a literal value OR pick a
 * workspace variable/secret. When the form value is ``$workspace/<name>``
 * the chip UI is shown instead of the input; clearing the chip restores the
 * regular input (empty).
 */
export function WorkspaceSecretRefField({
  name,
  label,
  helperText,
  tooltip,
  disabled,
  required,
  rightOffsetPx = 4,
  children,
}: WorkspaceSecretRefFieldProps) {
  const { watch, setValue } = useFormContext();
  const value = watch(name);
  const { spotGroup } = useSpotUser();

  const refName = parseWorkspaceRef(value);

  const pickFromPicker = (variableName: string) => {
    setValue(name, formatWorkspaceRef(variableName), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const clearRef = () => {
    setValue(name, "", { shouldDirty: true, shouldValidate: true });
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label != null && (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip || undefined}
          isRequired={required}
        />
      )}
      {refName ? (
        <WorkspaceRefChip
          variableName={refName}
          onClear={clearRef}
          disabled={disabled}
          groupId={spotGroup?.id}
        />
      ) : (
        <div className="group relative">
          {children}
          <div
            className="absolute inset-y-0 flex items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
            style={{ right: rightOffsetPx }}
          >
            <WorkspaceSecretRefPicker
              groupId={spotGroup?.id ?? ""}
              onPick={pickFromPicker}
              disabled={disabled || !spotGroup?.id}
            />
          </div>
        </div>
      )}
      {helperText && !refName && (
        <span className="text-xs text-muted-foreground">{helperText}</span>
      )}
    </div>
  );
}
